import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { default as Actions } from '../actions';
import * as CommonActions from '../../common/actions';
import * as Util from '../../common/utilities/';
import { default as config } from '../../utilities/config';

import { BlockConfiguration, BlockError } from '../components';
import { default as FlagsProvider } from '../../flags/Flags';
import { Spinner } from '@salesforce/design-system-react';

import './ContentBlock.scss';

export class ContentBlock extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadedMessageBlocks: false,
      loadedSpotlightData: false
    };
  }
  componentDidMount() {
    this.props.actions.getAssetTypes({ ignoreErrors: true });
    this.props.actions.getAssetDistributionByKey(
      'type',
      {},
      {},
      {},
      {
        ignoreErrors: true
      }
    );
    this.props.actions.getAssetAttributes();
    this.props.actions.getBlockDataFromSdk();
    this.props.actions.getCentralDataFromSdk();
    this.props.actions.getRules();
  }

  handleConfigurationChange(field, data) {
    this.props.actions.updateForm(field, data);
    if (field === 'selection') {
      const block = this.selectionToMetalMastodonBlock(data);
      if (block.asset_type) {
        this.props.actions.getRandomAsset(block.asset_type, {
          ignoreErrors: true
        });
      }
    }
  }

  selectionToMetalMastodonBlock(selection) {
    const subjectLine = selection.subjectLine || false;
    const assetType = selection.assetType?.selection?.[0]?.id;
    const { messageId } = selection;

    const spotlight = {};
    if (selection.spotlight) {
      if (Array.isArray(selection.spotlight)) {
        selection.spotlight.forEach(attr => {
          if (attr.key && attr.value) {
            spotlight[attr.key] = attr.value;
          }
        });
      } else if (
        selection.spotlight?.asset_value &&
        selection.spotlight?.asset_key
      ) {
        spotlight.asset_key = selection.spotlight.asset_key;
        spotlight.asset_value = selection.spotlight.asset_value;
      }
    }

    return {
      block_id: selection.id,
      asset_type: assetType,
      message_id: messageId,
      selection_mode: 'popular',
      control_subject_line: subjectLine,
      spotlight
    };
  }

  /**
   * Returns false if all of the below are true:
   *  - className is defined
   *  - assetClass is found
   *  - assetClass is weather aware
   *  - weather is enabled
   *  - the location map is null or empty
   *
   * Returns true otherwise
   */
  isValidWeatherClass(className) {
    if (className?.length) {
      const assetClass = this.props.assets.data.types?.find(
        type => type.name === className
      );
      const settings = this.props.rules.data?.rules?.settings || {};
      const locationMap = this.props.rules.data?.rules?.location_mapping;

      if (assetClass?.weather_aware && settings.weather) {
        return locationMap && Object.keys(locationMap).length > 1;
      }
    }

    return true;
  }

  componentDidUpdate(prevProps) {
    // Block data was loaded from blocksdk, convert saved block back to selection
    if (Util.propChanged(prevProps, this.props, 'block.isDataLoading', false)) {
      const data = this.props.block?.data;
      if (data) {
        const isExistingBlock = !!data.id;
        this.props.actions.updateForm('selection', {
          ...data,
          isExistingBlock
        });

        // load spotlight data:
        // - asset attribute values for each spotlight key
        // - distribution by attribute key
        if (
          isExistingBlock &&
          data.spotlight &&
          Object.keys(data.spotlight).length
        ) {
          this.props.actions.getAssetDistributionByBlock(data.id);

          let spotlight = data.spotlight;
          //handle spotlight for existing blocks
          if (data.spotlight?.asset_key && data.spotlight?.asset_value) {
            this.props.actions.getAssetAttributeValues(
              data.spotlight?.asset_key
            );
          } else {
            spotlight.forEach(spot =>
              this.props.actions.getAssetAttributeValues(spot.key)
            );
          }
        } else {
          this.setState({ loadedSpotlightData: true });
        }
      }
    }

    // Distribution needs to be loaded before unblocking the spinner
    if (
      !this.state.loadedSpotlightData &&
      Util.propChanged(
        prevProps,
        this.props,
        'block.isDistributionLoading',
        false
      )
    ) {
      this.setState({ loadedSpotlightData: true });
    }

    // Message blocks loaded
    if (Util.propChanged(prevProps, this.props, 'block.messageBlocks')) {
      this.setState({ loadedMessageBlocks: true });
    }

    if (config.releaseStatus === 'DEV' && !this.state.loadedMessageBlocks) {
      this.setState({ loadedMessageBlocks: true, loadedSpotlightData: true });
    }

    // Central block data was loaded from blocksdk, update form with messageId, fetch other blocks
    if (
      Util.propChanged(
        prevProps,
        this.props,
        'block.isCentralDataLoading',
        false
      )
    ) {
      const messageId = this.props.block.centralData?.messageId;
      this.props.actions.updateForm('selection', { messageId });
      this.props.actions.getMessageBlocks(messageId);
    }

    // Form was changed, save data to blocksdk and save to mm
    if (Util.propChanged(prevProps, this.props, 'forms.selection')) {
      // always save the block data back into the sdk on any change
      this.props.actions.saveBlockDataInSdk(this.props.forms.selection);

      // if assetType or messageId changed, also save in MM
      // Future configuration options might go here
      if (
        Util.propChanged(prevProps, this.props, 'forms.selection.assetType') ||
        Util.propChanged(prevProps, this.props, 'forms.selection.subjectLine')
      ) {
        // Only try to save to MM if there is an assetType
        const block = this.selectionToMetalMastodonBlock(
          this.props.forms.selection
        );
        if (block.asset_type?.length) {
          this.props.actions.saveBlock(block, {
            ignoreErrors: true,
            cToken: this.props.session.cToken
          });
        }
      }

      // Check that each spotlight node has a value before saving
      if (
        Util.propChanged(prevProps, this.props, 'forms.selection.spotlight')
      ) {
        if (
          this.props.forms.selection.spotlight &&
          Object.entries(this.props.forms.selection.spotlight).every(
            spotlight => !!spotlight[1].key && !!spotlight[1].value
          )
        ) {
          // Only try to save to MM if there is an assetType
          const block = this.selectionToMetalMastodonBlock(
            this.props.forms.selection
          );
          if (block.asset_type?.length) {
            this.props.actions.saveBlock(block, {
              ignoreErrors: true,
              cToken: this.props.session.cToken
            });
          }
        }
      }

      // if display height or width changed, update content
      if (
        Util.propChanged(prevProps, this.props, 'forms.selection.height') ||
        Util.propChanged(prevProps, this.props, 'forms.selection.width') ||
        Util.propChanged(prevProps, this.props, 'forms.selection.maxWidth') ||
        Util.propChanged(prevProps, this.props, 'forms.selection.alignment') ||
        Util.propChanged(prevProps, this.props, 'forms.selection.altText')
      ) {
        if (this.props.block?.page?.id) {
          // page.id is the block_id
          const settings = this.props.rules?.data?.rules?.settings || {};
          const flags = this.props.flags?.flags || {};

          const height = this.props.forms?.selection?.height || 'auto';
          const width = this.props.forms?.selection?.width || 'auto';
          const maxWidth = this.props.forms?.selection?.maxWidth || '100%';
          const altText = this.props.forms?.selection?.altText || '';
          const dimensions = {
            height: height,
            width: width,
            maxWidth: maxWidth,
            altText: altText
          };
          const alignment = this.props.forms?.selection?.alignment || 'left';
          const options = {
            ...settings,
            ...flags,
            ...dimensions,
            alignment
          };

          this.props.actions.setContentInSdk(this.props.block.page, options);
        }
      }
    }

    // After block is saved to mm, it will return the block_id and the message_id
    // Copy this id over to 'selection' form so it will get persisted to block SDK data
    // Also save message_id in central data so that future blocks will use the same id
    if (Util.propChanged(prevProps, this.props, 'block.isSaving', false)) {
      if (this.props.block?.page?.id) {
        // page.id is the block_id
        const messageId = this.props.block.messageId;
        const spotlightDistribution = this.props.block.spotlight_distribution;
        const settings = this.props.rules?.data?.rules?.settings || {};
        const flags = this.props.flags?.flags || {};

        const height = this.props.forms?.selection?.height || 'auto';
        const width = this.props.forms?.selection?.width || 'auto';
        const maxWidth = this.props.forms?.selection?.maxWidth || '100%';
        const altText = this.props.forms?.selection?.altText || '';
        const dimensions = {
          height: height,
          width: width,
          maxWidth: maxWidth,
          altText: altText
        };
        const alignment = this.props.forms?.selection?.alignment || 'left';
        const options = { ...settings, ...flags, ...dimensions, alignment };

        this.props.actions.updateForm('selection', {
          id: this.props.block.page.id,
          messageId,
          spotlightDistribution
        });
        this.props.actions.setContentInSdk(this.props.block.page, options);
        this.props.actions.saveCentralDataInSdk({ messageId });
      }
    }
  }

  error() {
    if (!this.props.assets.isLoading && this.props.assets.hasLoadedOnce) {
      if (this.props.assets.error) {
        return 'session';
      } else if (
        this.props.assets.data.types &&
        this.props.assets.data.types.length <= 0
      ) {
        return 'noassets';
      }
    }
    return false;
  }

  renderSpinner() {
    return (
      <div className="spinner">
        <Spinner
          size="large"
          variant="brand"
          assistiveText={{ label: 'Large spinner' }}
        />
      </div>
    );
  }

  renderError(err) {
    return (
      <div className="block-config-home">
        <BlockError error={err} />
      </div>
    );
  }

  renderBlock() {
    return (
      <FlagsProvider>
        <div className="block-config-home">
          <BlockConfiguration
            actions={this.props.actions}
            assetTypes={this.props.assets.data.types}
            attributes={this.props.assets.data.keys}
            attributeValues={this.props.assets.data.values}
            messageBlocks={this.props.block.messageBlocks}
            distribution={this.props.assets.data.distribution}
            randomAsset={this.props.assets.data.random}
            page={this.props.block.page}
            forms={this.props.forms}
            formChange={this.handleConfigurationChange.bind(this)}
            isValidWeather={this.isValidWeatherClass(
              this.props.forms.selection?.assetType?.selection?.[0]?.id
            )}
          />
        </div>
      </FlagsProvider>
    );
  }

  render() {
    const err = this.error();
    if (err) {
      return this.renderError(err);
    } else {
      const loading =
        this.props.assets.isLoading ||
        !this.state.loadedMessageBlocks ||
        !this.state.loadedSpotlightData;
      return loading ? this.renderSpinner() : this.renderBlock();
    }
  }
}

ContentBlock.contextTypes = {
  router: PropTypes.object
};

ContentBlock.propTypes = {
  actions: PropTypes.shape({
    getAssetDistributionByKey: PropTypes.func,
    getAssetDistributionByBlock: PropTypes.func,
    getAssetAttributes: PropTypes.func,
    getAssetAttributeValues: PropTypes.func,
    getAssetTypes: PropTypes.func,
    getBlockDataFromSdk: PropTypes.func,
    getUserDataFromSdk: PropTypes.func,
    saveBlock: PropTypes.func,
    setContentInSdk: PropTypes.func,
    setSuperContentInSdk: PropTypes.func,
    saveBlockDataInSdk: PropTypes.func,
    updateForm: PropTypes.func
  }),
  assets: PropTypes.object,
  block: PropTypes.object,
  form: PropTypes.object,
  modals: PropTypes.object,
  location: PropTypes.object,
  session: PropTypes.object
};

function mapStateToProps(state) {
  return {
    assets: state.ecs.assets,
    block: state.ecs.block,
    flags: state.ecs.flags,
    forms: state.forms,
    modals: state.modals,
    rules: state.ecs.rules,
    session: state.i18n.session
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...Actions, ...CommonActions }, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ContentBlock);
